import { z } from 'zod';

export const AIM_LICENSE_SKIP_MESSAGE =
  'Skipping AIM: license missing or expired';

export const licenseAdditionalFeatureNames = ['aim'] as const;

export type LicenseAdditionalFeatureName =
  (typeof licenseAdditionalFeatureNames)[number];

export const licenseCcuStrategies = ['devices', 'ccu'] as const;

export type LicenseCcuStrategy = (typeof licenseCcuStrategies)[number];

const licenseFeatureSchema = z.strictObject({
  validFrom: z.number(),
  validTo: z.number(),
});

const verifiedLicenseFeatureSchema = licenseFeatureSchema.extend({
  expired: z.boolean(),
});

const additionalFeaturesSchema = z
  .strictObject({
    // Keys must match licenseAdditionalFeatureNames.
    aim: licenseFeatureSchema.optional(),
  })
  .optional();

const verifiedAdditionalFeaturesSchema = z
  .strictObject({
    aim: verifiedLicenseFeatureSchema.optional(),
  })
  .optional();

export const licenseFileSchema = z.object({
  _key: z.string(), // encryption key for credentials etc.
  ccu: z.number().default(0),
  ccuStrategy: z.enum(licenseCcuStrategies).default('devices'),
  customerName: z.string(),
  devices: z.number(),
  expiry: z.number(),
  glipWebhookCustomerURL: z.string(),
  licenseKey: z.string(),
  privKey: z.string(),
  pubKey: z.string(),
  validTo: z.string(),
  additionalFeatures: additionalFeaturesSchema,
});

const verifiedLicenseSchema = licenseFileSchema
  .omit({ additionalFeatures: true })
  .extend({
    additionalFeatures: verifiedAdditionalFeaturesSchema,
  });

export type LicenseFile = z.infer<typeof licenseFileSchema>;
export type VerifiedLicense = z.infer<typeof verifiedLicenseSchema>;

export function verifyLicenseData(data: unknown) {
  return licenseFileSchema.passthrough().safeParse(data);
}

export const toVerifiedLicense = (
  license: LicenseFile,
  now = Date.now(),
): VerifiedLicense => ({
  ...license,
  additionalFeatures: license.additionalFeatures?.aim
    ? {
        aim: {
          ...license.additionalFeatures.aim,
          expired:
            now > license.additionalFeatures.aim.validTo ||
            now < license.additionalFeatures.aim.validFrom,
        },
      }
    : undefined,
});

export const isAimLicenseValid = (
  license: Pick<VerifiedLicense, 'expiry' | 'additionalFeatures'>,
  now = Date.now(),
): boolean =>
  license.expiry > now && license.additionalFeatures?.aim?.expired === false;
